import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon, PaperAirplaneIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { InfiniteData } from '@tanstack/react-query';

import { useIssueResolver } from 'hooks/useIssueResolver';
import { RelationshipsResponseDTO, useInfiniteRelationships } from 'services/repositories/relationships/relationships';
import { BulkActionVariables, useRecoverTransactions } from 'services/repositories/transactions/transactions';
import { useCurrentUser } from 'services/repositories/user/user';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { DATA_CHECK_ERROR_CODE } from 'support/helpers/const/const';
import { FEATURE_FLAGS, usePartnerFeatureFlag } from 'support/helpers/featureFlags/featureFlags';
import { routeToPage, routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { isSupplierInRelationship } from 'support/helpers/relationships/relationships';
import {
  CompanyType,
  Connectors,
  DocumentType,
  IssueResolverType,
  MessageV2DTO,
  PartnerDTO,
  RelationshipStatus,
  TransactionFlowState,
  TransactionInternalType,
  TransactionOriginApp,
  TransactionsRoutes,
  ValueOf,
} from 'support/types';
import { DeliverTransactionsModal } from 'pages/Transactions/components/modals/DeliverTransactionsModal';
import { DropTransactionModal } from 'pages/Transactions/components/modals/DropTransactionModal';
import { DisplayModes } from 'components/DataTable/Types';
import { Button } from 'components/Form/Button';
import { MenuButton, MenuButtonOption } from 'components/MenuButton/MenuButton';
import { PriceList } from 'components/WebEDI/PriceList/PriceList';

import { ActionButtons } from './ActionButtons/ActionButtons';

type MessageHeaderProps = {
  message: MessageV2DTO;
  messageOriginApp: TransactionOriginApp;
  currentPartner: PartnerDTO | undefined;
  relatedTransactions: Array<MessageV2DTO> | undefined;
  mode: DisplayModes;
  isLoading?: boolean;
};

export function MessageHeader({
  message,
  messageOriginApp,
  relatedTransactions,
  currentPartner,
  mode,
  isLoading,
}: MessageHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { data: currentUser } = useCurrentUser();

  const isPartnerReceiver = message.receiver_partner?.id === currentPartner?.id;

  const hasDatachecksError = message?.blocking_error?.code === DATA_CHECK_ERROR_CODE;
  const issueResolver = useIssueResolver(message?.issue_resolver_id);

  const [isDropTransactionModalOpen, setIsDropTransactionModalOpen] = useState(false);
  const [isManualDeliverTransactionModalOpen, setIsManualDeliverTransactionModalOpen] = useState(false);

  const { mutate, isLoading: isRecoveringTransaction } = useRecoverTransactions();

  const retrigger = useCallback(async () => {
    if (!message) {
      return;
    }
    const payload: BulkActionVariables = {
      items: [{ transactionId: message.id }],
    };

    mutate(payload, {
      onSuccess: () => {
        track('transaction_recovered', {
          transaction_id: message.id,
          type: TransactionInternalType.message,
        });

        addNotification({
          title: t('webedi:notifications.recover.successful'),
          subtitle: t('webedi:notifications.recover.successful_description'),
        });
      },
      onError: () => {
        addNotification(t('webedi:notifications.recover.failed'), NotificationType.error);
      },
    });
  }, [message, mutate, t, track]);

  const otherPartnerId =
    currentPartner && message && message.sender_partner?.id === currentPartner?.id
      ? message.receiver_partner?.id
      : message.sender_partner?.id;

  const receiverPartnerID = message.receiver_partner?.id;

  const { data: relationships } = useInfiniteRelationships({
    variables: {
      query: {
        perPage: '100',
        filterValues: [
          {
            key: 'partner_id',
            value: otherPartnerId || '',
          },
        ],
      },
    },
    enabled: Boolean(otherPartnerId && currentPartner),
  });

  const isDocumentTypeAllowedForPartner =
    (partner: PartnerDTO, allRelationships: InfiniteData<RelationshipsResponseDTO>) =>
    (documentType: ValueOf<DocumentType>) => {
      if (documentType !== DocumentType.invoice) {
        return true;
      }

      if (!partner.companyType?.includes(CompanyType.SUPPLIER)) {
        return false;
      }

      const allRelationshipsFlat =
        allRelationships?.pages.flatMap((page) =>
          page.data?.filter((rel) =>
            [RelationshipStatus.ACTIVE, RelationshipStatus.LIVE].includes(RelationshipStatus[rel.status]),
          ),
        ) || [];

      return isSupplierInRelationship(allRelationshipsFlat, partner.id);
    };

  const enabledDocumentsTypes = useMemo(() => {
    return relationships && currentPartner && message.flow !== TransactionFlowState.test
      ? getRelationshipsAsWebEDISender(relationships, currentPartner.id)
          .map((relationship) => relationship.message_type)
          .filter(isDocumentTypeAllowedForPartner(currentPartner, relationships))
      : [];
  }, [currentPartner, message.flow, relationships]);

  const { customer_can_retry: canRetry, type: documentType, blocking_error: blockingError } = message;
  const canDropTransaction = blockingError ? message.customer_can_drop_message : null;

  const onEditCancel = useCallback(() => {
    navigate(routeToTransactionPage(TransactionInternalType.message, message.id), { state: { keepBreadcrumbs: true } });
  }, [message.id, navigate]);

  const { enabled: priceCatalogsEnabled } = usePartnerFeatureFlag(FEATURE_FLAGS.TEMPORARY_PRICE_CATALOGS);

  const actions = useMemo(() => {
    let finalActions: Array<React.ReactNode> = [];
    if (canDropTransaction) {
      let action = (
        <Button
          LeftIcon={TrashIcon}
          analyticsId="transactions:discard_message"
          variant="secondary"
          onClick={() => setIsDropTransactionModalOpen(true)}
          key={`discard-message-${message.id}`}
        >
          {t('transactions:bulk_discard_modal.action')}
        </Button>
      );

      // show option to mark as manually imported if partner is receiver
      if (isPartnerReceiver && !hasDatachecksError && issueResolver === IssueResolverType.CURRENT_PARTNER) {
        action = (
          <MenuButton
            key={`close-issue-${message.id}`}
            text={t('transactions:view.discard_menu')}
            variant="secondary"
            analyticsId="transactions:close_issue"
            size="base"
          >
            <MenuButtonOption
              onClick={() => {
                setIsManualDeliverTransactionModalOpen(true);
              }}
              className="flex-col border-b border-gray-100 last:border-b-0 "
              analyticsId="transactions:manually_deliver"
            >
              <div className="max-w-80 py-1 text-left">
                <p className="mb-2 text-sm font-medium text-gray-900">{t('transactions:bulk_discard_manual.title')}</p>
                <p className="text-sm text-gray-500">{t('transactions:bulk_discard_manual.description')}</p>
              </div>
            </MenuButtonOption>
            <MenuButtonOption
              onClick={() => {
                setIsDropTransactionModalOpen(true);
              }}
              className="flex-col border-b border-gray-100 last:border-b-0 "
              analyticsId="transactions:drop"
            >
              <div className="max-w-80 py-1 text-left">
                <p className="mb-2 text-sm font-medium text-gray-900">{t('transactions:bulk_discard.title')}</p>
                <p className="text-sm text-gray-500">{t('transactions:bulk_discard.description')}</p>
              </div>
            </MenuButtonOption>
          </MenuButton>
        );
      }

      finalActions.push(action);
    }

    if (canRetry) {
      finalActions.push(
        <Button
          variant="secondary"
          onClick={retrigger}
          LeftIcon={ArrowPathIcon}
          loading={isRecoveringTransaction}
          key={`retry-${message.id}-${isRecoveringTransaction}`}
          analyticsId="webedi:document_retry"
        >
          {t('transactions:list.bulk_retrigger')}
        </Button>,
      );
    }

    if (
      blockingError?.type === 'DATA' &&
      (issueResolver === IssueResolverType.CURRENT_PARTNER || currentUser?.isAdmin)
    ) {
      finalActions.push(
        <Button
          variant="secondary"
          key={`edit-${message.id}`}
          onClick={() => {
            navigate(routeToPage(TransactionsRoutes.edit, { id: message.id }), { state: { keepBreadcrumbs: true } });
          }}
          LeftIcon={PencilSquareIcon}
          analyticsId="webedi:document_edit"
        >
          {t('webedi:viewDocument.editCTA')}
        </Button>,
      );
    }

    if (finalActions.length === 0) {
      finalActions.push(
        <ActionButtons
          key={`action-buttons-${message.id}`}
          message={message}
          messageType={documentType}
          messageOriginApp={messageOriginApp}
          relatedTransactions={relatedTransactions}
          enabledDocumentTypes={enabledDocumentsTypes as Array<DocumentType>}
        />,
      );
    }

    if (mode === DisplayModes.edit) {
      finalActions = [];
      // add apply pricelist if invoice
      if ((documentType === DocumentType.invoice || documentType === DocumentType.creditNote) && priceCatalogsEnabled) {
        finalActions.push(<PriceList receiverPartnerId={receiverPartnerID} key={`price-list-${receiverPartnerID}`} />);
      }
      finalActions.push([
        <Button
          variant="secondary"
          onClick={onEditCancel}
          disabled={isLoading}
          key={`cancel-${message.id}`}
          analyticsId="webedi:edit_cancel"
        >
          {t('common:cancel')}
        </Button>,
        <Button
          variant="primary"
          type="submit"
          form="webedi-document"
          LeftIcon={PaperAirplaneIcon}
          loading={isLoading}
          key={`edit-${message.id}`}
          analyticsId="webedi:edit_send"
        >
          {t('webedi:editResend')}
        </Button>,
      ]);
    }

    return <div className="flex items-center space-x-2">{finalActions}</div>;
  }, [
    canDropTransaction,
    canRetry,
    blockingError?.type,
    issueResolver,
    currentUser?.isAdmin,
    mode,
    message,
    t,
    isPartnerReceiver,
    hasDatachecksError,
    retrigger,
    isRecoveringTransaction,
    navigate,
    documentType,
    messageOriginApp,
    relatedTransactions,
    enabledDocumentsTypes,
    priceCatalogsEnabled,
    onEditCancel,
    isLoading,
    receiverPartnerID,
  ]);

  return (
    <>
      {actions}

      <DropTransactionModal
        transactionIds={[message.id]}
        open={isDropTransactionModalOpen}
        onComplete={() => {
          setIsDropTransactionModalOpen(false);
        }}
      />

      <DeliverTransactionsModal
        transactionIds={[message.id]}
        open={isManualDeliverTransactionModalOpen}
        onComplete={() => {
          setIsManualDeliverTransactionModalOpen(false);
        }}
      />
    </>
  );
}

const getRelationshipsAsWebEDISender = (
  relationships: InfiniteData<RelationshipsResponseDTO>,
  currentPartnerId: string,
) => {
  return (
    relationships?.pages.flatMap(
      (page) =>
        page.data?.filter(
          (relationship) =>
            relationship.sender_partner?.id === currentPartnerId &&
            [RelationshipStatus.ACTIVE, RelationshipStatus.LIVE].includes(RelationshipStatus[relationship.status]) &&
            Connectors.WEB_EDI === relationship.sender_integration?.production?.connector,
        ) || [],
    ) || []
  );
};
